import React from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Table from "@/components/atoms/Table";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useGetClaimsAll, putUpdateClaim } from "@/services/Claims";
import { Claim, ClaimStatusType } from "@/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Text from "@/components/atoms/Text";
import CTA from "@/components/atoms/CTA";
import ClaimsAdminDrawer from "./Drawer";
import Badge, { badgeColor } from "@/components/atoms/Badge";
import Loader from "@/components/atoms/Loader";
import Group from "@/components/atoms/Group";
import ClaimStatusEnum from "@/constants/enums/ClaimStatusEnum";

import styles from "./styles.module.css";
import PageWrapper from "../../molecules/PageWrapper";
import SortableHeader from "@/components/molecules/SortableHeader";
import dayjs from "dayjs";

const ClaimsAdmin = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [selectedClaim, setSelectedClaim] = useState<Claim | null>(null);

  const { data: claimsData, isFetching: isFetchingClaimsData } = useGetClaimsAll();

  const { mutateAsync: updateClaimStatus } = useMutation({
    mutationFn: async (claim: Claim) => {
      const updatedClaim = { ...claim, status: ClaimStatusEnum.UNDER_REVIEW };
      await putUpdateClaim(updatedClaim);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["getClaimsAll"] });
    },
  });

  const openClaimsDrawerHandler = (claim: Claim) => {
    setSelectedClaim(claim);
    if (claim.status === ClaimStatusEnum.SUBMITTED) {
      updateClaimStatus(claim);
    }
  };

  const statusToBadgeColor = (status: ClaimStatusType) => {
    switch (status) {
      case ClaimStatusEnum.SUBMITTED:
        return badgeColor.warning;
      case ClaimStatusEnum.UNDER_REVIEW:
        return badgeColor.primary;
      case ClaimStatusEnum.ACCEPTED:
      case ClaimStatusEnum.RESOLVED:
        return badgeColor.success;
      case ClaimStatusEnum.REJECTED:
        return badgeColor.error;
      default:
        return badgeColor.primary;
    }
  };

  const columns: ColumnDef<any>[] = useMemo(
    () => [
      {
        header: ({ column }) => <SortableHeader column={column} label={t("common:date")} />,
        accessorKey: "created",
        cell: ({ row }) => dayjs(row.original.created).format("DD.MM.YYYY"),
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("claims:issuer")} />,
        accessorKey: "createdByCompany.companyName",
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("claims:counterparty")} />,
        accessorKey: "counterPartyCompany.companyName",
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("product:material")} />,
        accessorKey: "order.salesItem.salesItem",
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("orders:totalPrice")} />,
        accessorKey: "order.totalPrice",
        cell: ({ row }) => <div>{row.original.order.totalPrice["NOK"]}</div>,
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("commonAdmin:status")} />,
        accessorKey: "status",
        cell: ({ row }) => (
          <Badge radius="xs" size="lg" color={statusToBadgeColor(row.original.status)}>
            {t("enumerables:claimStatus." + row.original.status)}
          </Badge>
        ),
      },
      {
        header: () => <Table.Th>{t("orders:actions")}</Table.Th>,
        accessorKey: "handlinger",
        cell: ({ row }) => (
          <CTA intent="secondary" onClick={() => openClaimsDrawerHandler(row.original)}>
            {row.original.status === ClaimStatusEnum.UNDER_REVIEW || row.original.status === ClaimStatusEnum.SUBMITTED
              ? t("claims:review")
              : t("common:details")}
          </CTA>
        ),
      },
    ],
    []
  );

  const table = useReactTable({
    data: claimsData ? claimsData : [],
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <>
      <title>{`Reviver - ${t("claims:claims")}`}</title>
      <meta name="description" content={`Reviver - ${t("claims:claims")}`} />

      <PageWrapper>
        <div className={styles.adminClaimsContent}>
          <Group>
            <Text size="xl">{t("claims:claims")}</Text>
            {isFetchingClaimsData ? <Loader /> : null}
          </Group>

          <Table className={styles.adminClaimsTable}>
            <Table.Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Table.Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Table.Th
                      key={header.id}
                      colSpan={header.colSpan}
                      style={{
                        width: header.getSize(),
                      }}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </Table.Th>
                  ))}
                </Table.Tr>
              ))}
            </Table.Thead>
            <Table.Tbody>
              {table.getRowModel().rows.map((row) => (
                <Table.Tr
                  key={row.id}
                  style={{
                    backgroundColor:
                      row.original.status === ClaimStatusEnum.SUBMITTED
                        ? "var(--color-warning-secondary)"
                        : "transparent",
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Table.Td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Table.Td>
                  ))}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          <ClaimsAdminDrawer
            claim={selectedClaim}
            isOpen={selectedClaim !== null}
            onClose={() => setSelectedClaim(null)}
          />
        </div>
      </PageWrapper>
    </>
  );
};

export default ClaimsAdmin;
